import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TextField, MenuItem, TextFieldProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PT_TIMEZONE as PT_TZ } from '../../constants/dateTime';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export type DateOnlyPickerProps = {
  value?: string | null;
  onChange: (val: string | null) => void;
  label?: string;
  disableFuture?: boolean;
  textFieldProps?: TextFieldProps;
};

export const DateOnlyPicker: React.FC<DateOnlyPickerProps> = ({
  value,
  onChange,
  label = 'Date',
  disableFuture = false,
  textFieldProps,
}) => {
  const { theme } = useThemeContext();

  const day = React.useMemo<Dayjs | null>(() => {
    if (!value) return null;

    const d = dayjs(value, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
    return d.isValid() ? d : null;
  }, [value]);

  const handle = (newVal: Dayjs | null) => {
    if (!newVal || !newVal.isValid()) return onChange(null);

    onChange(newVal.format('YYYY-MM-DD'));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={day}
        onChange={handle}
        disableFuture={disableFuture}
        format="MM/DD/YYYY"
        slotProps={{
          textField: {
            fullWidth: true,
            ...textFieldProps,
            sx: {
              backgroundColor: theme.layout.defaults.backgroundColor,
              ...(textFieldProps?.sx || {}),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export type TimeSelectProps = {
  value: string;
  onChange: (val: string) => void;
  stepMinutes?: 5 | 10 | 15 | 20 | 30;
  label?: string;
  disabled?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'select' | 'value' | 'onChange' | 'label'>;
};

function buildOptions(step: number) {
  const opts: { v: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      opts.push({ v: `${hh}:${mm}`, label: `${hour12}:${mm} ${ampm}` });
    }
  }
  return opts;
}

export const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onChange,
  stepMinutes = 15,
  label = 'Time',
  disabled,
  textFieldProps,
}) => {
  const { theme } = useThemeContext();
  const options = React.useMemo(() => buildOptions(stepMinutes), [stepMinutes]);

  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      disabled={disabled}
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        ...(textFieldProps?.sx || {}),
      }}
      {...textFieldProps}
    >
      {options.map((o) => (
        <MenuItem key={o.v} value={o.v}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const combineDateTime = (dateStr?: string | null, timeStr?: string | null): string | null => {
  if (!dateStr || !timeStr) return null;
  const asPt = dayjs.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', PT_TZ);
  return asPt.isValid() ? asPt.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
};

export const extractDate = (iso?: string | null): string => {
  if (!iso) return '';
  const direct = dayjs(iso);
  if (direct.isValid()) {
    return direct.tz(PT_TZ).format('YYYY-MM-DD');
  }
  const fallback = dayjs(iso, ['YYYY-MM-DD HH:mm:ss.SSS ZZ', 'YYYY-MM-DD HH:mm:ss ZZ'], true);
  if (!fallback.isValid()) return '';
  return fallback.tz(PT_TZ).format('YYYY-MM-DD');
};

export const extractTime = (iso?: string | null): string => {
  if (!iso) return '';
  const direct = dayjs(iso);
  if (direct.isValid()) {
    return direct.tz(PT_TZ).format('HH:mm');
  }
  const fallback = dayjs(iso, ['YYYY-MM-DD HH:mm:ss.SSS ZZ', 'YYYY-MM-DD HH:mm:ss ZZ'], true);
  if (!fallback.isValid()) return '';
  return fallback.tz(PT_TZ).format('HH:mm');
};

export default { DateOnlyPicker, TimeSelect, combineDateTime, extractDate, extractTime };
